import { useEffect, useRef, useState } from 'react';

import { useHistory } from 'react-router-dom';

import { login } from 'data-providers/authProvider';
import { setAuthCookie, setIncCookies } from 'utils/helpers';
import * as localStorage from 'utils/local-storage';

import { AUTH_ERRORS, errorsInitState, inputInitState, renderFlagsInitState } from './constants';

export const useLoginFormPresenter = () => {
  const history = useHistory();
  const passwordInputRef = useRef(null);

  const [authData, setAuthData] = useState({ ...inputInitState });
  const [errors, setErrors] = useState({ ...errorsInitState });
  const [renderFlags, setRenderFlags] = useState({ ...renderFlagsInitState });

  const getLoginError = () => {
    return errors.loginError || (errors.error ? true : false);
  };

  const getPasswordError = () => {
    return !renderFlags.passwordFirstRender && (errors.passwordError || errors.error);
  };

  const setError = (field, value) => {
    setErrors((prev) => {
      return { ...prev, [field]: value };
    });
  };

  const setRenderFlag = (field, value) => {
    setRenderFlags((prev) => {
      return { ...prev, [field]: value };
    });
  };

  const validateLogin = () => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(authData.login);
  };

  const validatePassword = () => {
    return authData.password.length > 0;
  };

  const onLoginChangeHandler = (event) => {
    const value = event.target.value.replace(/\s/g, '');

    setAuthData((prev) => {
      return { ...prev, login: value };
    });

    if (renderFlags.loginFirstRender) {
      setError('loginError', '');
    } else {
      setError('loginError', '');
      setError('error', '');
    }
  };

  const onPasswordChangeHandler = (event) => {
    const value = event.target.value.replace(/\s/g, '');

    setAuthData((prev) => {
      return { ...prev, password: value };
    });

    setError('passwordError', '');
    setError('error', '');
    setRenderFlag('passwordFirstRender', false);
  };

  const onLoginBlurHandler = () => {
    const isLoginValid = validateLogin();

    if (isLoginValid) {
      return setError('loginError', '');
    }

    const error = authData.login.length > 0 ? AUTH_ERRORS.INVALID_FORMAT : AUTH_ERRORS.EMPTY_LOGIN;

    setError('loginError', error);
  };

  const onPasswordBlurHandler = () => {
    setRenderFlag('passwordFirstRender', false);

    const isPasswordValid = validatePassword();

    if (!isPasswordValid) {
      setError('passwordError', AUTH_ERRORS.EMPTY_PASSWORD);
    } else {
      setError('passwordError', '');
    }
  };

  const doLogin = () => {
    login({ login: authData.login, password: authData.password })
      .then((data) => {
        setAuthCookie(data.token, data.refresh_token);
        setIncCookies(data.cookies);
        localStorage.saveItem('token', {
          token: data.token,
          refresh: data.refresh_token,
          expires: data.expires_in,
        });
        history.push('/applications/list');

        setAuthData({ ...inputInitState });
        setErrors({ ...errorsInitState });
      })
      .catch(() => {
        setError('error', AUTH_ERRORS.LOGIN_AND_PASSWORD_ERROR);
      });
  };

  const onHandleSubmit = () => {
    setErrors({ ...errorsInitState });
    onLoginBlurHandler();
    onPasswordBlurHandler();

    const isLoginValid = validateLogin();
    const isPasswordValid = validatePassword();

    if (authData.login && isLoginValid && authData.password && isPasswordValid) {
      doLogin();
    }
  };

  const onNextButtonClick = (firstRender) => {
    if (firstRender) {
      onLoginBlurHandler();

      if (authData.login && !getLoginError()) {
        setRenderFlag('loginFirstRender', false);
      }
    } else {
      setAuthData({ ...inputInitState });
      setErrors({ ...errorsInitState });
      setRenderFlag('loginFirstRender', true);
    }
  };

  const onHandleKeyDown = (event) => {
    if (/\s/.test(event.key)) {
      event.preventDefault();
    }

    if (event?.key === 'Enter') {
      onLoginBlurHandler();

      if (!renderFlags.passwordFirstRender) {
        onPasswordBlurHandler();
      }

      const isLoginValid = validateLogin();
      const isPasswordValid = validatePassword();

      if (renderFlags.loginFirstRender) {
        if (authData.login && isLoginValid) {
          setRenderFlag('loginFirstRender', false);
        }
      } else if (authData.login && isLoginValid && authData.password && isPasswordValid) {
        onHandleSubmit();
      }
    }
  };

  useEffect(() => {
    if (!renderFlags.loginFirstRender) {
      return;
    }

    const isLoginValid = validateLogin();

    if (!authData.login || !isLoginValid || getLoginError()) {
      return;
    }

    setRenderFlag('loginFirstRender', false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData.login, renderFlags.loginFirstRender]);

  useEffect(() => {
    setAuthData({ ...inputInitState });
    setErrors({ ...errorsInitState });
  }, []);

  useEffect(() => {
    if (!renderFlags.loginFirstRender) {
      setTimeout(() => {
        if (passwordInputRef.current) {
          passwordInputRef.current.focus();
        }
      }, 0);
    }
  }, [renderFlags.loginFirstRender]);

  return {
    authData,
    loginFirstRender: renderFlags.loginFirstRender,
    validateLogin,
    validatePassword,
    getLoginError,
    getPasswordError,
    onLoginChangeHandler,
    onPasswordChangeHandler,
    onLoginBlurHandler,
    onPasswordBlurHandler,
    onHandleKeyDown,
    onHandleSubmit,
    onNextButtonClick,
  };
};
